/**
 * Knowledge-base schemas for a single BuildIntel ATO ruleset version.
 *
 * WHY these types exist here (rather than in `types/models/support/`):
 *  - The `AtoRulesetVersion` model in `types/models/support/` is the row
 *    that lives in the DB — a POINTER at a set of rules with a version
 *    stamp. It intentionally holds NO tax numbers.
 *  - The types below describe the CONTENT of a ruleset — rates,
 *    thresholds, PCG references. Every content module in
 *    `src/tax/rulesets/<version>/` must implement `AtoKnowledgeBase`.
 *
 * All numbers are captured as an `AtoRateValue<Unit>` object so the unit
 * is inseparable from the number. This prevents subtle bugs where a "0.67"
 * meant "cents / km" in one place and "dollars / hour" in another —
 * everything now travels with an explicit unit.
 *
 * Status field:
 *  - `"scaffold"`  → this section has been staked out but no live numbers
 *    should be read by production code yet. `getKnowledge()` will warn
 *    if a consumer tries to.
 *  - `"confirmed"` → a human has reviewed the ATO source and the numbers
 *    below are the ones we want the app to use.
 */

import { z } from "zod";

/* ------------------------------------------------------------------ */
/* Rate value — number + unit, indivisible                             */
/* ------------------------------------------------------------------ */

export const AtoRateValueSchema = z.object({
  amount: z.number().finite(),
  unit: z.string().min(1), // e.g. "AUD/hour", "AUD/km", "AUD", "percent", "AUD/year"
  /**
   * Freeform authority note — where the number came from (bulletin,
   * ruling title, ATO web page). Kept short; long refs live in
   * `references`.
   */
  source: z.string().min(1),
});
export type AtoRateValue = z.infer<typeof AtoRateValueSchema>;

/* ------------------------------------------------------------------ */
/* Section: rates                                                     */
/* Recurring numbers a tradie hits inside a normal FY.                */
/* ------------------------------------------------------------------ */

export const AtoRatesSchema = z.object({
  status: z.enum(["scaffold", "confirmed"]),
  /** GST rate applied to sales/purchases. Stored as decimal (0.10 = 10%). */
  gst: AtoRateValueSchema,
  /** WFH fixed-rate method — dollars per hour worked from home. */
  wfhFixedRate: AtoRateValueSchema,
  /** Vehicle cents-per-km method — dollars per business km. */
  centsPerKm: AtoRateValueSchema,
  /**
   * Concessional super contributions cap (per member per financial year).
   * `null` when the ruleset intentionally omits it (e.g. sole traders
   * without employees may not surface this).
   */
  superConcessionalCap: AtoRateValueSchema.nullable(),
});
export type AtoRates = z.infer<typeof AtoRatesSchema>;

/* ------------------------------------------------------------------ */
/* Section: thresholds                                                */
/* Bright-line numbers that flip behaviour on/off.                    */
/* ------------------------------------------------------------------ */

export const AtoThresholdsSchema = z.object({
  status: z.enum(["scaffold", "confirmed"]),
  /** Turnover above which GST registration is compulsory. */
  gstRegistration: AtoRateValueSchema,
  /**
   * Instant asset write-off cap. `null` when the ATO has not legislated
   * a value for this ruleset's window (defaults to the ordinary
   * depreciation regime).
   */
  instantAssetWriteOff: AtoRateValueSchema.nullable(),
  /** BAS lodgement — small-business turnover ceiling. */
  smallBusinessBasCeiling: AtoRateValueSchema,
  /**
   * PAYG withholding registration threshold. Optional — many tradies
   * are sole traders and never hit this.
   */
  paygWithholdingRegistration: AtoRateValueSchema.nullable(),
});
export type AtoThresholds = z.infer<typeof AtoThresholdsSchema>;

/* ------------------------------------------------------------------ */
/* Section: references                                                */
/* PCGs, TRs, TDs, ATO web pages — one row each with URL + summary.   */
/* ------------------------------------------------------------------ */

export const AtoReferenceSchema = z.object({
  /**
   * ATO identifier ("PCG 2023/1", "TR 97/23", "TD 2024/1"), or a stable
   * page slug for web references.
   */
  code: z.string().min(1),
  /** One-liner: what the tradie needs to know from this ruling. */
  summary: z.string().min(1),
  /** Canonical URL on ato.gov.au. */
  url: z.string().url(),
  /** Optional bucket for filtering in the Knowledge Base UI. */
  topic: z
    .enum(["wfh", "vehicle", "gst", "bas", "depreciation", "records", "other"])
    .default("other"),
});
export type AtoReference = z.infer<typeof AtoReferenceSchema>;

export const AtoReferencesSchema = z.object({
  status: z.enum(["scaffold", "confirmed"]),
  items: z.array(AtoReferenceSchema),
});
export type AtoReferences = z.infer<typeof AtoReferencesSchema>;

/* ------------------------------------------------------------------ */
/* Section: effective dates                                           */
/* When this ruleset applies. Independent of the registry's           */
/* effectiveFrom (which is the "we started using this on X" instant); */
/* this is the tax-period window the CONTENT covers.                  */
/* ------------------------------------------------------------------ */

export const AtoEffectiveWindowSchema = z.object({
  /** Financial year start (AU FY = 1 Jul). ISO date. */
  financialYearStart: z.string(),
  /** Financial year end (30 Jun of the following calendar year). ISO date. */
  financialYearEnd: z.string(),
  /**
   * Free note — helpful when a ruleset covers a partial year or spans
   * a mid-FY change (e.g. "PCG 2023/1 fixed-rate updated 2024-11-01").
   */
  note: z.string().default(""),
});
export type AtoEffectiveWindow = z.infer<typeof AtoEffectiveWindowSchema>;

/* ------------------------------------------------------------------ */
/* Root — a full knowledge base for one version                        */
/* ------------------------------------------------------------------ */

export const AtoKnowledgeBaseSchema = z.object({
  version: z.string(),
  rates: AtoRatesSchema,
  thresholds: AtoThresholdsSchema,
  references: AtoReferencesSchema,
  effective: AtoEffectiveWindowSchema,
});
export type AtoKnowledgeBase = z.infer<typeof AtoKnowledgeBaseSchema>;
